import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Input } from "../../Input";
import { Select } from "../../Select";
import { Toggle } from "../../Toggle";
import { Button } from "../../../Buttons/Button";
import { ShipCard } from "../../../Cards/ShipCard";
import { FormLabel } from "../../FormLabel";
import { LoadingLayout } from "../../../Layouts/LoadingLayout";
import { fromCountries } from "../../../../data/countries";
import { ITEM_OPTIONS } from "../../../../data/items";
import { quoteSchema } from "../../validations/quote";
import {
  createQuoteRequest,
  getExistingAddressOptions,
  getZipCodeInputLabel,
  mapCountriesToSelectOptions,
} from "../../../../helpers";
import {
  useGetCountries,
  useGetUserAddresses,
} from "../../../../api/hooks/index";
import { MeasurementSelect } from "../../MeasurementSelect";
import { Combobox } from "../../Combobox";
import { Alert } from "../../../Alerts/Alert";

export function QuoteForm({
  getQuote,
  setQuoteRequest,
  isLoading,
  isError,
  error,
}) {
  const { handleSubmit, control, watch, setValue } = useForm({
    defaultValues: {
      isExistingAddress: false,
      selectedAddress: {},
      from: { country: fromCountries[0], zip: "" },
      to: { country: fromCountries[0], zip: "", isResidential: false },
      item: {
        type: ITEM_OPTIONS[0],
        length: "",
        width: "",
        height: "",
        weight: "",
        isImperial: true,
      },
    },
    resolver: yupResolver(quoteSchema),
  });

  const { data: addressData, isLoading: isGetUserAddressLoading } =
    useGetUserAddresses();

  const { data: countries = [], isLoading: countriesLoading } =
    useGetCountries();

  const countryOptions = mapCountriesToSelectOptions(countries);

  useEffect(() => {
    if (!addressData?.addresses) return;
    const existingAddresses =
      getExistingAddressOptions(addressData.addresses) || [];
    if (existingAddresses.length > 0) {
      setValue("isExistingAddress", true);
      setValue("selectedAddress", existingAddresses[0]);
    }
  }, [addressData, setValue]);

  if (isGetUserAddressLoading || countriesLoading) return <LoadingLayout />;

  const existingAddresses =
    getExistingAddressOptions(addressData?.addresses) || [];
  const isExistingAddress = watch("isExistingAddress");
  const itemType = watch("item.type");
  const isPackage = itemType.name === "Package";
  const isImperial = watch("item.isImperial");

  // const fromCountry = watch("from.country");
  const toCountry = watch("to.country");

  // const fromCountryData = countriesMap[fromCountry.id] || {};
  // const toCountryData = countriesMap[toCountry.id] || {};

  // const showFromInput = fromCountryData.hasZip || fromCountryData.hasPostal;
  // const showToInput = toCountryData.hasZip || toCountryData.hasPostal;

  // const fromLabel = fromCountryData.hasZip ? "Zip Code" : fromCountryData.hasPostal ? "Postal Code" : "Code";
  // const toLabel = toCountryData.hasZip ? "Zip Code" : toCountryData.hasPostal ? "Postal Code" : "Code";

  const onSubmit = async (data) => {
    const quoteRequest = createQuoteRequest(data);
    try {
      const quotes = await getQuote(quoteRequest);
      const from = data.isExistingAddress
        ? {
            isExistingAddress: true,
            existingAddress: data.selectedAddress,
            shouldSaveAddress: false,
          }
        : quotes.request.from;

      setQuoteRequest({ ...quotes.request, from });
    } catch {}
  };

  return (
    <>
      {/* --- Show Alert if there is an error --- */}
      {isError && error && (
        <div className="mb-4">
          <Alert code={error.code} message={error.message} />
        </div>
      )}

      {/* --- Sender & Receiver Cards --- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* --- Sender Card --- */}
        <ShipCard>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
            <FormLabel title="Sender Details" />
            {existingAddresses.length > 0 && (
              <div className="mt-2 sm:mt-0">
                <Toggle
                  control={control}
                  name="isExistingAddress"
                  leftLabel="Use Existing Address?"
                />
              </div>
            )}
          </div>
          <div className="grid grid-cols-12 gap-4 items-center">
            {isExistingAddress ? (
              <div className="col-span-12">
                <Select
                  options={existingAddresses}
                  control={control}
                  name="selectedAddress"
                  label="Sender Address"
                />
              </div>
            ) : (
              <>
                <div className="col-span-12 sm:col-span-8">
                  <Combobox
                    disabled
                    options={fromCountries}
                    name="from.country"
                    label="Country"
                    control={control}
                  />
                </div>
                <div className="col-span-12 sm:col-span-4">
                  <Input
                    label={"Postal Code"}
                    control={control}
                    name="from.zip"
                  />
                </div>
              </>
            )}
          </div>
        </ShipCard>

        {/* --- Receiver Card --- */}
        <ShipCard>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
            <FormLabel title="Receiver Details" />
          </div>
          <div className="grid grid-cols-12 gap-4 items-center">
            <div className="col-span-12 sm:col-span-8">
              <Select
                options={countryOptions}
                name="to.country"
                label="Country"
                control={control}
              />
            </div>
            <div className="col-span-12 sm:col-span-4">
              <Input
                label={getZipCodeInputLabel(toCountry?.id)}
                control={control}
                name="to.zip"
              />
            </div>
          </div>
        </ShipCard>
      </div>

      {/* --- Item Card --- */}
      <ShipCard className="mt-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
          <FormLabel title="Item Details" />
          {isPackage && (
            <div className="mt-2 sm:mt-0">
              <MeasurementSelect control={control} name="item.isImperial" />
            </div>
          )}
        </div>
        <div className="grid grid-cols-12 gap-4 items-center">
          <div className="col-span-12 sm:col-span-3">
            <Select
              options={ITEM_OPTIONS}
              label="Item Type"
              control={control}
              name="item.type"
            />
          </div>
          {isPackage && (
            <>
              <div className="col-span-6 sm:col-span-2">
                <Input
                  label="Length"
                  control={control}
                  name="item.length"
                  addOnRight={isImperial ? "in" : "cm"}
                />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <Input
                  label="Width"
                  control={control}
                  name="item.width"
                  addOnRight={isImperial ? "in" : "cm"}
                />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <Input
                  label="Height"
                  control={control}
                  name="item.height"
                  addOnRight={isImperial ? "in" : "cm"}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <Input
                  label="Weight"
                  control={control}
                  name="item.weight"
                  addOnRight={isImperial ? "lbs" : "kg"}
                />
              </div>
            </>
          )}
        </div>
      </ShipCard>

      {/* --- Submit Button --- */}
      <div className="mt-6 flex justify-end">
        <Button
          onClick={handleSubmit(onSubmit)}
          text="Get Quote"
          isLoading={isLoading}
        />
      </div>
    </>
  );
}
